import React, { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CalendarClock, ChevronLeft, ChevronRight, BatteryLow, Battery, CheckCircle2,
  BellOff, Undo2, Loader2,
} from "lucide-react";
import api from "@/lib/api";

const URGENCY = {
  soon: { color: "#EF4444", label: "Plan soon" },
  scheduled: { color: "#F59E0B", label: "Scheduled" },
  ok: { color: "#34D399", label: "Plenty left" },
};
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function ymd(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function fmt(iso) {
  try { return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function Servicing() {
  const qc = useQueryClient();
  const [cursor, setCursor] = useState(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); });
  const [selectedDay, setSelectedDay] = useState(null);
  const [busy, setBusy] = useState(null);

  const { data } = useQuery({ queryKey: ["servicing-calendar"], queryFn: () => api.get("/health/servicing-calendar").then((r) => r.data) });

  const items = data?.items || [];
  const todayIso = data?.today;

  // group swaps by replace_by date
  const byDate = useMemo(() => {
    const m = {};
    for (const it of items) { (m[it.replace_by] ||= []).push(it); }
    return m;
  }, [items]);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthSwaps = items.filter((it) => it.replace_by.startsWith(`${year}-${String(month + 1).padStart(2, "0")}`) && !it.snoozed);
  const upcoming = items.filter((it) => !it.snoozed && it.replace_by >= (todayIso || "")).slice(0, 8);
  const snoozed = items.filter((it) => it.snoozed);

  const snooze = async (it) => {
    setBusy(it.device_id);
    try {
      await api.post("/health/battery/snooze", { store_id: it.store_id, device_id: it.device_id });
      toast.success(`${it.chair} marked ordered — off the report until due`);
      qc.invalidateQueries({ queryKey: ["servicing-calendar"] });
      qc.invalidateQueries({ queryKey: ["daily-health"] });
    } catch { toast.error("Couldn't snooze — check your access to this store"); }
    setBusy(null);
  };
  const unsnooze = async (it) => {
    setBusy(it.device_id);
    try {
      await api.post("/health/battery/unsnooze", { store_id: it.store_id, device_id: it.device_id });
      toast.success(`${it.chair} back on the report`);
      qc.invalidateQueries({ queryKey: ["servicing-calendar"] });
      qc.invalidateQueries({ queryKey: ["daily-health"] });
    } catch { toast.error("Couldn't update — check your access to this store"); }
    setBusy(null);
  };

  const dayList = selectedDay ? (byDate[selectedDay] || []) : [];

  return (
    <div data-testid="servicing-page">
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Servicing</div>
      <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">Battery servicing calendar</h1>
      <p className="mt-2 text-sm text-slate-400 max-w-2xl">
        Every upcoming battery swap across your stores in one month view — so you can order and schedule ahead. Mark one <strong className="text-slate-300">ordered</strong> and it drops off the morning report until it's due.
      </p>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar */}
        <div className="lg:col-span-2 surface-card p-6">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-[#38BDF8]" />
              <h2 className="font-display text-lg font-medium" data-testid="cal-month-label">{MONTHS[month]} {year}</h2>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="cal-prev" onClick={() => { setCursor(new Date(year, month - 1, 1)); setSelectedDay(null); }}
                className="rounded-lg border border-[#2A3550] bg-[#1E2638] p-2 hover:border-[#38BDF8] transition-colors duration-200"><ChevronLeft className="h-4 w-4" /></button>
              <button data-testid="cal-today" onClick={() => { const n = new Date(); setCursor(new Date(n.getFullYear(), n.getMonth(), 1)); setSelectedDay(null); }}
                className="rounded-lg border border-[#2A3550] bg-[#1E2638] px-3 py-2 text-xs font-medium hover:border-[#38BDF8] transition-colors duration-200">Today</button>
              <button data-testid="cal-next" onClick={() => { setCursor(new Date(year, month + 1, 1)); setSelectedDay(null); }}
                className="rounded-lg border border-[#2A3550] bg-[#1E2638] p-2 hover:border-[#38BDF8] transition-colors duration-200"><ChevronRight className="h-4 w-4" /></button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-1.5 text-center">
            {DOW.map((d) => <div key={d} className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold py-1">{d}</div>)}
            {cells.map((d, i) => {
              if (!d) return <div key={`e${i}`} />;
              const iso = ymd(new Date(year, month, d));
              const swaps = (byDate[iso] || []).filter((x) => !x.snoozed);
              const isToday = iso === todayIso;
              const active = iso === selectedDay;
              const worst = swaps.reduce((a, x) => (x.urgency === "soon" ? "soon" : a === "soon" ? "soon" : x.urgency === "scheduled" ? "scheduled" : a), "ok");
              return (
                <button key={iso} data-testid={`cal-day-${iso}`} onClick={() => setSelectedDay(active ? null : iso)}
                  className={`relative aspect-square rounded-lg border text-sm flex flex-col items-center justify-center transition-colors duration-150
                    ${active ? "border-[#38BDF8] bg-[#38BDF8]/10" : isToday ? "border-[#38BDF8]/40 bg-[#171E2E]" : "border-[#232C40] bg-[#141B29] hover:border-[#334063]"}`}>
                  <span className={isToday ? "text-[#38BDF8] font-semibold" : "text-slate-300"}>{d}</span>
                  {swaps.length > 0 && (
                    <span className="mt-1 inline-flex items-center gap-0.5">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ background: (URGENCY[worst] || URGENCY.scheduled).color }} />
                      <span className="text-[10px] text-slate-400">{swaps.length}</span>
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {selectedDay && (
            <div className="mt-5 border-t border-[#232C40] pt-4" data-testid="cal-day-detail">
              <div className="text-sm font-medium text-white mb-2">{fmt(selectedDay)} — {dayList.filter((x) => !x.snoozed).length} swap(s)</div>
              <div className="space-y-1.5">
                {dayList.map((it) => <SwapRow key={it.device_id} it={it} busy={busy} onSnooze={snooze} onUnsnooze={unsnooze} />)}
                {dayList.length === 0 && <div className="text-sm text-slate-400 py-2">Nothing scheduled this day.</div>}
              </div>
            </div>
          )}
        </div>

        {/* Side lists */}
        <div className="space-y-6">
          <div className="surface-card p-6" data-testid="servicing-upcoming">
            <div className="flex items-center gap-2 mb-3"><BatteryLow className="h-4 w-4 text-amber-400" /><h3 className="font-display text-base font-medium">Next up</h3></div>
            <div className="space-y-1.5">
              {upcoming.map((it) => <SwapRow key={it.device_id} it={it} busy={busy} onSnooze={snooze} onUnsnooze={unsnooze} />)}
              {data && upcoming.length === 0 && <div className="text-sm text-slate-400 py-2 flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-400" /> Nothing due — all healthy.</div>}
            </div>
          </div>

          {snoozed.length > 0 && (
            <div className="surface-card p-6" data-testid="servicing-snoozed">
              <div className="flex items-center gap-2 mb-3"><BellOff className="h-4 w-4 text-slate-400" /><h3 className="font-display text-base font-medium">Ordered / scheduled</h3></div>
              <div className="space-y-1.5">
                {snoozed.map((it) => <SwapRow key={it.device_id} it={it} busy={busy} onSnooze={snooze} onUnsnooze={unsnooze} />)}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SwapRow({ it, busy, onSnooze, onUnsnooze }) {
  const u = URGENCY[it.urgency] || URGENCY.scheduled;
  return (
    <div data-testid={`swap-row-${it.device_id}`}
      className={`flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-sm ${it.snoozed ? "border-[#232C40] bg-[#101725] opacity-70" : "border-[#232C40] bg-[#141B29]"}`}>
      <div className="min-w-0 flex items-center gap-2">
        <span className="h-2 w-2 rounded-full shrink-0" style={{ background: it.snoozed ? "#475569" : u.color }} />
        <div className="min-w-0">
          <div className="text-slate-200 truncate">#{it.store_number} · {it.chair}</div>
          <div className="text-[11px] text-slate-400 flex items-center gap-1">
            <Battery className="h-3 w-3" /> {it.battery}% · {it.snoozed ? "ordered" : `replace by ${fmt(it.replace_by)}`}
          </div>
        </div>
      </div>
      {it.snoozed ? (
        <button data-testid={`unsnooze-${it.device_id}`} disabled={busy === it.device_id} onClick={() => onUnsnooze(it)}
          className="inline-flex items-center gap-1 rounded-md border border-[#2A3550] bg-[#1E2638] px-2.5 py-1.5 text-xs text-slate-200 hover:border-[#38BDF8] transition-colors duration-200 disabled:opacity-50 shrink-0">
          {busy === it.device_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Undo2 className="h-3 w-3" />} Restore
        </button>
      ) : (
        <button data-testid={`snooze-${it.device_id}`} disabled={busy === it.device_id} onClick={() => onSnooze(it)}
          className="inline-flex items-center gap-1 rounded-md border border-[#2A3550] bg-[#1E2638] px-2.5 py-1.5 text-xs text-slate-200 hover:border-[#38BDF8] transition-colors duration-200 disabled:opacity-50 shrink-0">
          {busy === it.device_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <BellOff className="h-3 w-3" />} Ordered
        </button>
      )}
    </div>
  );
}
